/**
 * NavigationContext slice — manages the initialization lifecycle and active
 * selections for the MMS (Menu Management System) navigation.
 *
 * The initializationPhase state machine sequences app startup:
 *   idle → fetching-nav → nav-ready → fetching-tabs → complete
 * Components should gate rendering on isInitialized to avoid flash-of-empty-nav.
 *
 * selectMenuItem is a combined action that atomically updates activeMmsId,
 * selectedMenuKey, activeParentKey, and expandedMenuKeys in one dispatch to
 * prevent intermediate renders with a partially-updated menu state.
 *
 * resetNavigationContext is for hard resets (BA change); resetSelections is a
 * softer variant that preserves the initialization phase and nav tree so a
 * re-fetch isn't required just because the active item was cleared.
 */
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { NavigationTree } from '@/types/mms'
import { RootState } from '@/store'

/**
 * Initialization phases for the navigation flow
 * - idle: No initialization started
 * - fetching-nav: Fetching navigation tree from API
 * - nav-ready: Navigation tree loaded, ready to process
 * - fetching-tabs: Fetching DTS tabs for selected mmsId
 * - complete: All initialization complete, app ready
 */
export type InitializationPhase =
    | 'idle'
    | 'fetching-nav'
    | 'nav-ready'
    | 'fetching-tabs'
    | 'complete'

export interface NavigationContextState {
    // Initialization tracking
    initializationPhase: InitializationPhase
    isInitialized: boolean

    // Active selections (source of truth)
    activeMmsId: number | null
    activeDtsId: number | null

    // Menu state
    expandedMenuKeys: string[]
    selectedMenuKey: string | null
    activeParentKey: string | null
    selectedMenuName: string | null

    // Breadcrumb path (for future feature)
    breadcrumbPath: NavigationTree[]

    // URL sync control
    urlSyncEnabled: boolean

    // Error tracking
    initializationError: string | null
}

const initialState: NavigationContextState = {
    initializationPhase: 'idle',
    isInitialized: false,
    activeMmsId: null,
    activeDtsId: null,
    expandedMenuKeys: [],
    selectedMenuKey: null,
    activeParentKey: null,
    selectedMenuName: null,
    breadcrumbPath: [],
    urlSyncEnabled: true,
    initializationError: null,
}

export const navigationContextSlice = createSlice({
    name: 'navigationContext',
    initialState,
    reducers: {
        // Initialization phase management
        setInitializationPhase: (
            state,
            action: PayloadAction<InitializationPhase>,
        ) => {
            state.initializationPhase = action.payload
            if (action.payload === 'complete') {
                state.isInitialized = true
            }
        },

        setInitializationError: (
            state,
            action: PayloadAction<string | null>,
        ) => {
            state.initializationError = action.payload
        },

        // Active MMS ID management
        setActiveMmsId: (state, action: PayloadAction<number | null>) => {
            // Reset DTS when MMS actually changes
            if (state.activeMmsId !== action.payload) {
                state.activeDtsId = null
            }
            state.activeMmsId = action.payload
        },

        // Active DTS ID management
        setActiveDtsId: (state, action: PayloadAction<number | null>) => {
            state.activeDtsId = action.payload
        },

        // Menu expanded keys management
        setExpandedMenuKeys: (state, action: PayloadAction<string[]>) => {
            state.expandedMenuKeys = action.payload
        },

        addExpandedMenuKey: (state, action: PayloadAction<string>) => {
            if (!state.expandedMenuKeys.includes(action.payload)) {
                state.expandedMenuKeys.push(action.payload)
            }
        },

        removeExpandedMenuKey: (state, action: PayloadAction<string>) => {
            state.expandedMenuKeys = state.expandedMenuKeys.filter(
                (key) => key !== action.payload,
            )
        },

        toggleExpandedMenuKey: (state, action: PayloadAction<string>) => {
            const key = action.payload
            if (state.expandedMenuKeys.includes(key)) {
                state.expandedMenuKeys = state.expandedMenuKeys.filter(
                    (k) => k !== key,
                )
            } else {
                state.expandedMenuKeys.push(key)
            }
        },

        // Selected menu key management
        setSelectedMenuKey: (state, action: PayloadAction<string | null>) => {
            state.selectedMenuKey = action.payload
        },

        // Active parent key management (top-level menu section)
        setActiveParentKey: (state, action: PayloadAction<string | null>) => {
            state.activeParentKey = action.payload
        },

        // Selected menu name management
        setSelectedMenuName: (state, action: PayloadAction<string | null>) => {
            state.selectedMenuName = action.payload
        },

        // Breadcrumb path management
        setBreadcrumbPath: (state, action: PayloadAction<NavigationTree[]>) => {
            state.breadcrumbPath = action.payload
        },

        // URL sync control
        setUrlSyncEnabled: (state, action: PayloadAction<boolean>) => {
            state.urlSyncEnabled = action.payload
        },

        // Combined action for menu item selection
        selectMenuItem: (
            state,
            action: PayloadAction<{
                mmsId: number
                menuKey: string
                parentKey?: string
                expandedKeys?: string[]
            }>,
        ) => {
            const { mmsId, menuKey, parentKey, expandedKeys } = action.payload
            state.activeMmsId = mmsId
            state.activeDtsId = null // Reset DTS when selecting new menu item
            state.selectedMenuKey = menuKey
            if (parentKey !== undefined) {
                state.activeParentKey = parentKey
            }
            if (expandedKeys !== undefined) {
                state.expandedMenuKeys = expandedKeys
            }
        },

        // Reset navigation context (e.g., on BA change)
        resetNavigationContext: (state) => {
            state.initializationPhase = 'idle'
            state.isInitialized = false
            state.activeMmsId = null
            state.activeDtsId = null
            state.expandedMenuKeys = []
            state.selectedMenuKey = null
            state.activeParentKey = null
            state.breadcrumbPath = []
            state.initializationError = null
        },

        // Partial reset (keep navigation tree, reset selections)
        resetSelections: (state) => {
            state.activeMmsId = null
            state.activeDtsId = null
            state.selectedMenuKey = null
            state.selectedMenuName = null
            state.breadcrumbPath = []
        },
    },
})

export const {
    setInitializationPhase,
    setInitializationError,
    setActiveMmsId,
    setActiveDtsId,
    setExpandedMenuKeys,
    addExpandedMenuKey,
    removeExpandedMenuKey,
    toggleExpandedMenuKey,
    setSelectedMenuKey,
    setActiveParentKey,
    setSelectedMenuName,
    setBreadcrumbPath,
    setUrlSyncEnabled,
    selectMenuItem,
    resetNavigationContext,
    resetSelections,
} = navigationContextSlice.actions

// Selectors
export const selectInitializationPhase = (state: RootState) =>
    state.navigationContext.initializationPhase

export const selectIsInitialized = (state: RootState) =>
    state.navigationContext.isInitialized

export const selectActiveMmsId = (state: RootState) =>
    state.navigationContext.activeMmsId

export const selectActiveDtsId = (state: RootState) =>
    state.navigationContext.activeDtsId

export const selectExpandedMenuKeys = (state: RootState) =>
    state.navigationContext.expandedMenuKeys

export const selectSelectedMenuKey = (state: RootState) =>
    state.navigationContext.selectedMenuKey

export const selectActiveParentKey = (state: RootState) =>
    state.navigationContext.activeParentKey

export const selectSelectedMenuName = (state: RootState) =>
    state.navigationContext.selectedMenuName

export const selectBreadcrumbPath = (state: RootState) =>
    state.navigationContext.breadcrumbPath

export const selectUrlSyncEnabled = (state: RootState) =>
    state.navigationContext.urlSyncEnabled

export const selectInitializationError = (state: RootState) =>
    state.navigationContext.initializationError

export default navigationContextSlice.reducer
